<template>
  <HrPageLayout
    title="Appraisal Forms"
    subtitle="Upload and configure digitised appraisal forms by job group (H and below / J and above)"
    icon="📄"
    :breadcrumb-items="[{ label: 'Home', route: '/dashboard' }, { label: 'Performance Management' }, { label: 'Forms' }]"
  >
    <template #header-actions>
      <button type="button" class="hr-btn hr-btn-primary" @click.stop.prevent="openNew"><i class="bx bx-plus"></i> Add form</button>
    </template>

    <div class="pf">
      <p class="lead">
        Forms appear in Employee Self-service according to the staff member’s job group.
        After a cycle is launched, each employee is assigned the matching form automatically.
      </p>

      <div v-if="loading" class="muted pad">Loading forms…</div>
      <div v-else class="grid">
        <div v-for="f in forms" :key="f.id" class="pf-card">
          <div class="badge" :class="f.form_key">{{ formLabel(f.form_key) }}</div>
          <h4>{{ f.title }}</h4>
          <p>{{ f.guidance_text || 'No guidance text' }}</p>
          <small v-if="f.file_name">File: {{ f.file_name }}</small>
          <small v-else class="warn">No PDF/Word attached yet</small>
          <div class="acts">
            <button type="button" class="hr-btn hr-btn-sm" @click="edit(f)">Edit</button>
            <label class="hr-btn hr-btn-sm hr-btn-outline-primary file-btn">
              Upload file
              <input type="file" accept=".pdf,.doc,.docx" hidden @change="upload($event, f)">
            </label>
            <button type="button" class="hr-btn hr-btn-sm hr-btn-outline-danger" @click="deactivate(f)">Deactivate</button>
          </div>
        </div>
        <div v-if="!forms.length" class="pf-card empty">
          <h4>No forms configured</h4>
          <p>Create forms for Job Group H and below and Job Group J and above so staff see the right instrument in My Appraisals.</p>
          <button type="button" class="hr-btn hr-btn-primary" @click.stop.prevent="openNew">Create first form</button>
        </div>
      </div>
    </div>
  </HrPageLayout>

  <!-- Avoid Bootstrap .modal { display:none } -->
  <div
    v-if="show"
    class="pas-fs-overlay"
    role="dialog"
    aria-modal="true"
    aria-labelledby="pas-fs-title"
    @click.self="show = false"
  >
    <div class="pas-fs-dialog" @click.stop>
      <div class="pas-fs-head">
        <h3 id="pas-fs-title">{{ form.id ? 'Edit form' : 'New appraisal form' }}</h3>
        <button type="button" class="pas-fs-x" aria-label="Close" @click="show = false">&times;</button>
      </div>
      <div class="pas-fs-body">
        <label>Title * <input v-model="form.title" placeholder="e.g. PAS Form — Job Group H and below"></label>
        <label>Applies to job groups
          <select v-model="form.form_key">
            <option value="job_group_h_below">Job Group H and below</option>
            <option value="job_group_j_above">Job Group J and above</option>
          </select>
        </label>
        <label>Link to cycle (optional)
          <select v-model.number="form.cycle_id">
            <option :value="0">All cycles (default)</option>
            <option v-for="c in cycles" :key="c.id" :value="Number(c.id)">{{ c.cycle_name }}</option>
          </select>
        </label>
        <label>Guidance for employee / supervisor
          <textarea v-model="form.guidance_text" rows="4" placeholder="Instructions shown in ESS beside the form"></textarea>
        </label>
        <label>Digitised sections (one per line: Section title | Field prompt)
          <textarea v-model="sectionsText" rows="5" placeholder="Personal particulars | Confirm designation&#10;Targets | List KRAs&#10;Competencies | Rate core competencies"></textarea>
        </label>
        <p v-if="permHint" class="pas-fs-err">{{ permHint }}</p>
      </div>
      <div class="pas-fs-actions">
        <button type="button" class="hr-btn hr-btn-secondary" @click="show = false">Cancel</button>
        <button type="button" class="hr-btn hr-btn-primary" :disabled="saving" @click="save">{{ saving ? 'Saving…' : 'Save form' }}</button>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { execute, authUrl, toast } from "@/api";

export default {
  name: "PasFormsSetup",
  components: { HrPageLayout },
  data() {
    return {
      loading: false,
      saving: false,
      forms: [],
      cycles: [],
      show: false,
      permHint: "",
      form: { id: null, title: "", form_key: "job_group_h_below", cycle_id: 0, guidance_text: "" },
      sectionsText: ""
    };
  },
  mounted() {
    this.load();
  },
  methods: {
    formLabel(k) {
      return k === "job_group_j_above" ? "Job Group J+" : "Job Group H−";
    },
    async load() {
      this.loading = true;
      try {
        const fd = new FormData();
        fd.append("function", "pasListFormTemplates");
        const res = await execute(fd, authUrl);
        this.forms = (res?.data?.data?.forms || []).filter((f) => Number(f.is_active) !== 0);
        const fc = new FormData();
        fc.append("function", "pasListCycles");
        const cr = await execute(fc, authUrl);
        this.cycles = cr?.data?.data?.cycles || [];
      } finally {
        this.loading = false;
      }
    },
    openNew() {
      this.form = { id: null, title: "", form_key: "job_group_h_below", cycle_id: 0, guidance_text: "" };
      this.sectionsText =
        "Part A — Personal particulars | Confirm name, PF no, department, job group\n" +
        "Part B — Targets | Agree KRAs, objectives, weights (100%)\n" +
        "Part C — Competencies | Core values and behavioural competencies\n" +
        "Part D — Development | Training needs and recommendations";
      this.permHint = "";
      this.show = true;
    },
    edit(f) {
      this.form = {
        id: f.id,
        title: f.title || "",
        form_key: f.form_key || "job_group_h_below",
        cycle_id: Number(f.cycle_id || 0),
        guidance_text: f.guidance_text || ""
      };
      const secs = f.sections || [];
      this.sectionsText = secs.map((s) => `${s.title || ""} | ${s.prompt || ""}`).join("\n");
      this.permHint = "";
      this.show = true;
    },
    sectionsToJson() {
      return this.sectionsText
        .split("\n")
        .map((line) => line.trim())
        .filter(Boolean)
        .map((line) => {
          const [title, prompt] = line.split("|").map((x) => (x || "").trim());
          return { title: title || "Section", prompt: prompt || "" };
        });
    },
    async save() {
      if (!this.form.title) {
        toast.error("Title required");
        return;
      }
      this.saving = true;
      this.permHint = "";
      try {
        const fd = new FormData();
        fd.append("function", "pasSaveFormTemplate");
        if (this.form.id) fd.append("id", String(this.form.id));
        fd.append("title", this.form.title);
        fd.append("form_key", this.form.form_key);
        if (this.form.cycle_id) fd.append("cycle_id", String(this.form.cycle_id));
        fd.append("guidance_text", this.form.guidance_text || "");
        fd.append("sections_json", JSON.stringify(this.sectionsToJson()));
        const res = await execute(fd, authUrl);
        if (res?.data?.success) {
          toast.success("Form saved — employees see it based on job group");
          this.show = false;
          this.load();
        } else {
          const msg = res?.data?.message || "Save failed — ensure Head of Performance / APPRAISAL FORMS role is assigned";
          this.permHint = msg;
          toast.error(msg);
        }
      } catch (e) {
        const msg = e?.response?.data?.message || e?.message || "Save failed";
        this.permHint = msg;
        toast.error(msg);
      } finally {
        this.saving = false;
      }
    },
    async upload(e, f) {
      const file = e?.target?.files?.[0];
      if (!file || !f?.id) return;
      const fd = new FormData();
      fd.append("function", "pasUploadFormTemplateFile");
      fd.append("id", String(f.id));
      fd.append("file", file);
      const res = await execute(fd, authUrl);
      if (res?.data?.success) {
        toast.success("File uploaded");
        this.load();
      } else toast.error(res?.data?.message || "Upload failed");
      e.target.value = "";
    },
    async deactivate(f) {
      if (!window.confirm("Deactivate this form?")) return;
      const fd = new FormData();
      fd.append("function", "pasDeleteFormTemplate");
      fd.append("id", String(f.id));
      const res = await execute(fd, authUrl);
      if (res?.data?.success) {
        toast.success("Deactivated");
        this.load();
      } else toast.error(res?.data?.message || "Failed");
    }
  }
};
</script>

<style scoped>
.lead { color:#475569; font-size:.92rem; max-width:760px; margin:0 0 1rem; }
.grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(280px,1fr)); gap:1rem; }
/* .card renamed — Bootstrap .card collisions less critical but keep unique */
.pf-card { background:#fff; border:1px solid #e2e8f0; border-radius:14px; padding:1.1rem; display:flex; flex-direction:column; gap:.45rem; }
.pf-card h4 { margin:0; font-size:1rem; }
.pf-card p { margin:0; font-size:.88rem; color:#64748b; flex:1; }
.badge { align-self:flex-start; font-size:.7rem; font-weight:800; padding:.2rem .55rem; border-radius:999px; }
.badge.job_group_h_below { background:#ecfeff; color:#0e7490; }
.badge.job_group_j_above { background:#eff6ff; color:#1d4ed8; }
.acts { display:flex; flex-wrap:wrap; gap:.35rem; margin-top:.5rem; }
.file-btn { cursor:pointer; display:inline-flex; align-items:center; }
small { font-size:.78rem; color:#0369a1; }
.warn { color:#b45309 !important; }
.empty { align-items:flex-start; }
.muted { color:#94a3b8; }
.pad { padding:1.5rem; }

.pas-fs-overlay {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  width: 100vw;
  height: 100vh;
  background: rgba(15, 23, 42, 0.5);
  display: flex !important;
  align-items: center;
  justify-content: center;
  z-index: 10050 !important;
  padding: 1rem;
  box-sizing: border-box;
}
.pas-fs-dialog {
  background: #fff;
  width: min(560px, 96vw);
  border-radius: 14px;
  max-height: 92vh;
  overflow: auto;
  box-shadow: 0 25px 50px rgba(15, 23, 42, 0.25);
  display: block !important;
}
.pas-fs-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.15rem;
  border-bottom: 1px solid #e2e8f0;
  position: sticky;
  top: 0;
  background: #fff;
  z-index: 1;
}
.pas-fs-head h3 { margin: 0; font-size: 1.05rem; color: #0f172a; }
.pas-fs-x {
  border: none;
  background: none;
  font-size: 1.5rem;
  line-height: 1;
  cursor: pointer;
  color: #64748b;
  padding: 0 .25rem;
}
.pas-fs-body {
  padding: 1rem 1.15rem;
  display: flex;
  flex-direction: column;
  gap: .7rem;
}
.pas-fs-body label {
  display: flex;
  flex-direction: column;
  gap: .3rem;
  font-size: .75rem;
  font-weight: 700;
  color: #475569;
}
.pas-fs-body input,
.pas-fs-body select,
.pas-fs-body textarea {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: .5rem .65rem;
  background: #f8fafc;
  font-weight: 500;
  color: #0f172a;
}
.pas-fs-err {
  font-size: .85rem;
  color: #b91c1c;
  font-weight: 600;
  margin: 0;
}
.pas-fs-actions {
  display: flex;
  justify-content: flex-end;
  gap: .5rem;
  padding: 0 1.15rem 1rem;
}
</style>
